import React from 'react';
import styles from './Legal.module.css';

const PrivacyPolicy = () => {
  return (
    <div className={styles.legalPage}>
      <div className={styles.container}>
        <h1>Privacy Policy</h1>
        <p className={styles.lastUpdated}>Last Updated: March 28, 2026</p>

        <div className={styles.content}>
          <section>
            <h2>1. Introduction</h2>
            <p>
              Welcome to <strong>LeanWeek-AI</strong> ("we," "our," or "us"). We are committed to protecting your privacy 
              through our "Local-First" architecture. This Privacy Policy explains our practices regarding the 
              collection, use, and disclosure of information when you use our mobile application and website.
            </p>
            <p>
              By using LeanWeek-AI, you agree to the terms outlined in this policy. If you do not agree, 
              please do not use the application.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <p>
              LeanWeek-AI is designed to minimize data collection. We categorize the information we interact with 
              into two types:
            </p>
            <h3>A. User-Provided Fitness Data</h3>
            <p>
              To generate personalized 7-day fitness plans, the app asks for inputs such as your age, sex, weight, 
              height, fitness goals, and available equipment. <strong>This data is processed by our AI engine but 
              resides exclusively on your mobile device.</strong>
            </p>
            <h3>B. Technical and Usage Data</h3>
            <p>
              We may collect non-identifying information such as your device type, operating system version, 
              and basic app usage statistics (e.g., which features are used most frequently). This helps us 
              maintain app stability and prioritize new features.
            </p>
          </section>

          <section>
            <h2>3. Local Device Storage & Security</h2>
            <p>
              Unlike traditional cloud-based fitness apps, LeanWeek-AI stores your sensitive health biometrics 
              locally on your device's secure storage.
            </p>
            <ul>
              <li><strong>No Central Database:</strong> We do not maintain a server-side database of your weight or workout history.</li>
              <li><strong>User Control:</strong> You have the sole power to delete this data by resetting the app or uninstalling it.</li>
              <li><strong>Security:</strong> We rely on the native security features of iOS and Android to protect the data stored on your device.</li>
            </ul>
          </section>

          <section>
            <h2>4. AI Processing & Third Parties</h2>
            <p>
              Our AI Weekly Plan Generation uses a combination of localized rules and secure AI processing. 
              While biometric data may be sent to an AI processing endpoint to generate your plan, this data is 
              transient. It is used only for the duration of the generation request and is not retained or 
              used to train models by our third-party AI providers.
            </p>
          </section>

          <section>
            <h2>5. Health Data Permissions</h2>
            <p>
              LeanWeek-AI may request optional permissions to access <strong>Apple Health (HealthKit)</strong> 
              on iOS or <strong>Google Fit/Health Connect</strong> on Android. 
            </p>
            <p>
              If granted, we use this data solely to automate your daily step tracking and activity logs. 
              We do not upload this data to our servers, and we do not share it with any third parties for 
              advertising or marketing purposes.
            </p>
          </section>

          <section>
            <h2>6. Data Sharing & Disclosure</h2>
            <p>
              <strong>We do not sell, rent, or trade your personal data.</strong>
            </p>
            <p>
              The only instances where data may leave your device are:
            </p>
            <ul>
              <li><strong>User-Initiated Sharing:</strong> When you export "Share Cards" or PDF Recaps, you are choosing to share that data with your selected platforms.</li>
              <li><strong>Legal Requirements:</strong> If required by law to comply with a valid legal process.</li>
              <li><strong>Service Providers:</strong> Aggregated, non-identifiable technical data shared with analytics providers (e.g., Apple Telemetry) to monitor app performance.</li>
            </ul>
          </section>

          <section>
            <h2>7. User Rights (GDPR & CCPA)</h2>
            <p>
              We respect your rights under the General Data Protection Regulation (GDPR) and the California 
              Consumer Privacy Act (CCPA). Because your data is local:
            </p>
            <ul>
              <li><strong>Access & Portability:</strong> You can view all your data within the app interface.</li>
              <li><strong>Deletion:</strong> You can exercise your "Right to be Forgotten" by deleting the app or using the "Reset All Data" function in settings.</li>
            </ul>
          </section>

          <section>
            <h2>8. Children’s Privacy</h2>
            <p>
              LeanWeek-AI is not intended for use by individuals under the age of 18. We do not knowingly collect 
              personal data from children. If we become aware that a child has provided us with personal 
              information, we will take steps to delete such information immediately.
            </p>
          </section>

          <section>
            <h2>9. Medical Disclaimer</h2>
            <p>
              LeanWeek-AI is a fitness support tool and is <strong>not a medical device.</strong> The information 
              provided is for educational and motivational purposes only. Always consult with a qualified 
              healthcare professional before starting a new exercise or weight-loss program.
            </p>
          </section>

          <section>
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date at the top.
            </p>
          </section>

          <section>
            <h2>11. Contact Us</h2>
            <p>
              If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us at:
            </p>
            <p><strong>Email:</strong> support@leanweek-ai.com</p>
        </section>
          </div>
        </div>
      </div>
  );
};

export default PrivacyPolicy;